/**
 * verticalNav.ts — the grouped desktop sidebar for TRUCKING, SURFACE RENTALS and DOWNHOLE
 * TOOLS tenants.
 *
 * The same shape as CASING_SIDEBAR_STRUCTURE in casingNav.ts, and consumed the same way by
 * DesktopSidebar: an explicit whitelist of groups, each with a starting collapsed state.
 * The shared items (Clients, Equipment, Finance, Settings, …) are NOT redeclared here. They
 * are resolved by id from CASING_NAV, so a label or path fixed there is fixed on both
 * sidebars, and an unknown id throws rather than dropping silently.
 *
 * Who gets this sidebar: a tenant with `trucking`, `surface_rentals` or `downhole_tools` and
 * NOT casing (`usesVerticalSidebar` below). A casing tenant keeps the casing structure (with
 * its own TRUCKING / SURFACE RENTALS groups inserted, as before); a tenant with none of them
 * keeps the legacy sectioned list.
 *
 * PRESSURE TESTING joined with its first screens (fieldiq-api#907), the same way.
 * NDT INSPECTION joined with batch 1 (fieldiq-api#1134): technicians, welders,
 * instruments, sources and dosimetry; its settings are a Settings → Operations tab.
 * PIPELINE & FACILITY joined with PF-1 (fieldiq-api#1196): Sites, and the shared
 * Equipment register (no fork); its settings are a Settings → Operations tab too.
 * WELL TESTING joined with WT-1 (fieldiq-api#1241) as a group of its own, WELL
 * TESTING: Wells (WT-1), then Jobs, Packages, Equipment and Maintenance (WT-2,
 * fieldiq-api#1245); its settings are a Settings → Operations tab.
 * EARTHWORKS joined with EW-1 as a group of its own: Jobs, Sites, Rate Card,
 * then the shared Customers, Equipment and Maintenance; settings are an
 * Operations tab.
 * WELL SERVICING joined with WS-1 (fieldiq-api#1269) as a group of its own:
 * Wells, Rigs, Activity Codes, the shared Pressure Control register (BOP-1),
 * then the shared Customers, Equipment and Maintenance; settings are an
 * Operations tab.
 * WIRELINE & SLICKLINE joined with WL-1 (fieldiq-api#1336) as a group of its
 * own: Overview, Wells, Units, Lines, Tool Strings, then the shared Customers
 * and Equipment; settings are an Operations tab.
 *
 * DOWNHOLE TOOLS joined in the rebuild (fieldiq-api#882). Before that a downhole-only tenant
 * fell through to the legacy list, which has no Overview entry at all. Its screens are added
 * here as each one is rebuilt and routed — never ahead of its route.
 *
 * Every vertical item carries a positive `vertical` guard. A trucking-only tenant never sees
 * a rental item and vice versa, and a group whose items all fall away is not rendered.
 *
 * NEVER TWO ENTRIES FOR ONE PAGE. The resolver drops any later item whose path an
 * earlier item already claims. "Overview" is `/`, the widget Overview every vertical lands on
 * (api#873). The /rentals dashboard is retired (2026-10-15: one Overview for every vertical) and
 * so is the /trucking one (fix/trucking-nav): both redirect to `/`, whose Rentals Summary and
 * Trucking Dispatch widgets are the copies that stay. The same rule is why Dispatch is in
 * FAVORITES and not also in TRUCKING TOOLS.
 *
 * THE SAME RULE IS WHAT MAKES "TRUCKS & DRIVERS" A RENTALS-WITHOUT-TRUCKING ROW, with positive
 * guards only. Fleet, Drivers and Trip Inspections are listed twice under two ids: once in
 * OPERATIONS on `trucking`, and once in the Trucks & Drivers row on `surface_rentals`. A tenant
 * with trucking resolves OPERATIONS first and claims the three paths, so its row comes out empty
 * and is not drawn; a rentals tenant without trucking gets them in the row instead.
 *
 * Left out on purpose, as on the casing sidebar: Training, Timesheet reports, AI Agent (the
 * footer card is the way in) and Clock In. The trucking and rental report pages are reached
 * from the unified /reports catalog, which lists them under its Trucking and Rentals pills.
 * Every one of those routes still exists.
 */
import { CASING_NAV } from './casingNav';

export type NavVertical =
  | 'trucking' | 'surface_rentals' | 'downhole_tools' | 'pressure_testing' | 'ndt_inspection' | 'pipeline_facility' | 'well_testing'
  | 'earthworks' | 'well_servicing' | 'wireline_slickline';

/** The verticals this sidebar serves. Casing is never one of them: it has its own. */
export const VERTICAL_SIDEBAR_VERTICALS: NavVertical[] = [
  'trucking', 'surface_rentals', 'downhole_tools', 'pressure_testing', 'ndt_inspection', 'pipeline_facility', 'well_testing',
  'earthworks', 'well_servicing', 'wireline_slickline',
];

/** True for the tenants who get this sidebar (and the phone nav built from it). Positive guards only. */
export const usesVerticalSidebar = (hasVertical: (key: string) => boolean): boolean =>
  !hasVertical('casing_running') && VERTICAL_SIDEBAR_VERTICALS.some((v) => hasVertical(v));

/** Keys on GET /settings/trucking that switch a trucking screen off for a tenant. */
export type TruckingFeatureFlag = 'bol_enabled' | 'weight_tickets_enabled' | 'move_jobs_enabled';

export interface VerticalNavItem {
  id: string;
  label: string;
  /** lucide-react icon name; DesktopSidebar maps it to a component. */
  icon: string;
  path: string;
  /**
   * The vertical that shows this item, or a list where ANY one does (positive
   * checks only). Fleet, Drivers and Trip Inspections serve trucking and surface
   * rentals alike (#815, api#1041 / api#1043).
   */
  vertical?: NavVertical | readonly NavVertical[];
  /** Hidden only when the tenant's trucking settings say `false`; absent means shown. */
  feature?: TruckingFeatureFlag;
  /**
   * Shown only when GET /downhole/settings says this mode is ON. The opposite
   * default to `feature`: the screens it gates are new and additive, so an
   * item appearing before the answer would offer work a company has turned off.
   */
  downholeFeature?: DownholeFeatureFlag;
}

/** Downhole modes that switch whole screens on (GET /downhole/settings, api#1052). */
export type DownholeFeatureFlag = 'dd_mwd';

export const VERTICAL_NAV: VerticalNavItem[] = [
  { id: 'overview', label: 'Overview', icon: 'Home', path: '/' },

  // ── Trucking ──
  { id: 'trucking-dispatch', label: 'Dispatch', icon: 'Truck', path: '/trucking/dispatch', vertical: 'trucking' },
  { id: 'trucking-bols', label: 'Bills of Lading', icon: 'FileText', path: '/trucking/bols', vertical: 'trucking', feature: 'bol_enabled' },
  // Trucking's own entries. A rentals tenant without trucking reaches the same
  // three pages from the Trucks & Drivers row (the rentals- ids below).
  { id: 'trucking-drivers', label: 'Drivers', icon: 'Users', path: '/trucking/drivers', vertical: 'trucking' },
  { id: 'trucking-fleet', label: 'Fleet', icon: 'Wrench', path: '/trucking/fleet', vertical: 'trucking' },
  { id: 'trucking-trip-inspections', label: 'Trip Inspections', icon: 'ClipboardCheck', path: '/trucking/trip-inspections', vertical: 'trucking' },
  // Routed since pass 1 with no nav entry; it was the auto-landing page for a
  // trucking-only tenant, who could then never navigate back to it.
  { id: 'trucking-calendar', label: 'Calendar', icon: 'Calendar', path: '/trucking/calendar', vertical: 'trucking' },
  { id: 'trucking-weight-tickets', label: 'Weight Tickets', icon: 'ClipboardList', path: '/trucking/weight-tickets', vertical: 'trucking', feature: 'weight_tickets_enabled' },
  { id: 'trucking-move-jobs', label: 'Move Jobs', icon: 'ClipboardList', path: '/trucking/move-jobs', vertical: 'trucking', feature: 'move_jobs_enabled' },
  // The trucking rate card (api#950). "Trucking Rates", not "Rates": FINANCE
  // also lists Tool Rates for a tenant with downhole tools.
  { id: 'trucking-rates', label: 'Trucking Rates', icon: 'DollarSign', path: '/trucking/rates', vertical: 'trucking' },

  // ── Surface rentals ──
  { id: 'rentals-dispatch', label: 'Dispatch Board', icon: 'Truck', path: '/rentals/dispatch', vertical: 'surface_rentals' },
  // The order register (operator, 2026-10-15: "it's a register, not a dashboard").
  // The id and route keep the old name.
  { id: 'rentals-active', label: 'Rental Orders', icon: 'ClipboardList', path: '/rentals/active', vertical: 'surface_rentals' },
  // Shown as "Rental Equipment" (sweep batch 9); the id, route and component keep the old name.
  { id: 'rentals-assets', label: 'Rental Equipment', icon: 'Package', path: '/rentals/assets', vertical: 'surface_rentals' },
  { id: 'rentals-yard', label: 'Yard/Check-Out', icon: 'MapPin', path: '/rentals/yard', vertical: 'surface_rentals' },
  { id: 'rentals-inspections', label: 'Inspections', icon: 'ClipboardCheck', path: '/rentals/inspections', vertical: 'surface_rentals' },
  // Damage found on return, charged to the customer (#815 G): the "Needs pricing" queue.
  { id: 'rentals-damage-charges', label: 'Damage Charges', icon: 'ReceiptText', path: '/rentals/damage-charges', vertical: 'surface_rentals' },
  { id: 'rentals-tickets', label: 'Field Tickets', icon: 'FileText', path: '/rentals/tickets', vertical: 'surface_rentals' },
  { id: 'rentals-maintenance', label: 'Maintenance', icon: 'Wrench', path: '/rentals/maintenance', vertical: 'surface_rentals' },
  { id: 'rentals-price-books', label: 'Price Books', icon: 'DollarSign', path: '/rentals/price-books', vertical: 'surface_rentals' },
  // The trucks a rentals company hauls with (#815, api#1041 / api#1043): the
  // trucking pages, for the Trucks & Drivers row. Same paths as trucking's own
  // entries, which claim them first for a tenant with trucking.
  { id: 'rentals-fleet', label: 'Fleet', icon: 'Wrench', path: '/trucking/fleet', vertical: 'surface_rentals' },
  { id: 'rentals-drivers', label: 'Drivers', icon: 'Users', path: '/trucking/drivers', vertical: 'surface_rentals' },
  { id: 'rentals-trip-inspections', label: 'Trip Inspections', icon: 'ClipboardCheck', path: '/trucking/trip-inspections', vertical: 'surface_rentals' },

  // ── Downhole tools (fieldiq-api#882; icons from docs/downhole-rebuild.md) ──
  { id: 'downhole', label: 'Tool Dashboard', icon: 'LayoutDashboard', path: '/downhole', vertical: 'downhole_tools' },
  // Directional & MWD well jobs (api#1055): only once the company has the mode on.
  { id: 'downhole-wells', label: 'Wells', icon: 'MapPin', path: '/downhole/wells', vertical: 'downhole_tools', downholeFeature: 'dd_mwd' },
  { id: 'downhole-components', label: 'Components', icon: 'Wrench', path: '/downhole/components', vertical: 'downhole_tools' },
  { id: 'downhole-assemblies', label: 'Assemblies', icon: 'Layers', path: '/downhole/assemblies', vertical: 'downhole_tools' },
  { id: 'downhole-templates', label: 'Templates', icon: 'ScrollText', path: '/downhole/templates', vertical: 'downhole_tools' },
  { id: 'downhole-run-logs', label: 'Run Logs', icon: 'ClipboardList', path: '/downhole/run-logs', vertical: 'downhole_tools' },
  { id: 'downhole-work-orders', label: 'Work Orders', icon: 'Hammer', path: '/downhole/work-orders', vertical: 'downhole_tools' },
  // Field tickets (0461): the customer's sign-off before a run is billed.
  // "Tool Tickets", like Tool Invoices, so it never reads as the casing or
  // rentals Field Tickets for a tenant with both.
  { id: 'downhole-tickets', label: 'Tool Tickets', icon: 'FileText', path: '/downhole/tickets', vertical: 'downhole_tools' },
  // "Tool …" because the shared FINANCE row's Invoices and the REPORTS catalog
  // sit beside these for a downhole tenant, and two items called "Invoices"
  // would be indistinguishable.
  // "Ready to invoice", not "Tool Invoices" (Jason, 2026-10-30): FINANCE →
  // Invoices is the one invoice list for every vertical. Since the same day it
  // opens /downhole/invoices/ready, the billable runs by customer, and not
  // /downhole/invoices — that page is a list of invoices with the generate
  // dialog behind a button, so the label said one thing and the page did
  // another. The id is kept so saved sidebar layouts still place it.
  { id: 'downhole-invoices', label: 'Ready to invoice', icon: 'Receipt', path: '/downhole/invoices/ready', vertical: 'downhole_tools' },
  { id: 'downhole-reports', label: 'Tool Reports', icon: 'BarChart3', path: '/downhole/reports', vertical: 'downhole_tools' },
  // The rate card (api#906). "Tool Rates", not "Rates": FINANCE already lists
  // Rate Sheets for a tenant whose other vertical prices with them.
  { id: 'downhole-rates', label: 'Tool Rates', icon: 'DollarSign', path: '/downhole/rates', vertical: 'downhole_tools' },

  // ── Pressure testing (fieldiq-api#907). Icons from the existing sidebar sets,
  // so neither icon map changes for this vertical. "Calibration Alerts", not
  // "Alerts": the shared SYSTEM row already has an Alerts item (/alerts). ──
  // Test Jobs leads the PT group (fieldiq-api#915): the day's work, ahead of the register.
  { id: 'pt-jobs', label: 'Test Jobs', icon: 'ClipboardList', path: '/pressure-testing/jobs', vertical: 'pressure_testing' },
  { id: 'pt-instruments', label: 'Instruments', icon: 'ClipboardCheck', path: '/pressure-testing/instruments', vertical: 'pressure_testing' },
  { id: 'pt-alerts', label: 'Calibration Alerts', icon: 'Clock', path: '/pressure-testing/alerts', vertical: 'pressure_testing' },
  // Certificates (PT-4, fieldiq-api#921): the drafts awaiting issue, and the issued record.
  { id: 'pt-certificates', label: 'Certificates', icon: 'FileText', path: '/pressure-testing/certificates', vertical: 'pressure_testing' },
  // Field tickets (PT-5, fieldiq-api#932). "Test Tickets", not "Field Tickets": the
  // rentals group already has a Field Tickets item for a two-vertical tenant.
  { id: 'pt-tickets', label: 'Test Tickets', icon: 'Receipt', path: '/pressure-testing/tickets', vertical: 'pressure_testing' },
  // Invoices (PT-6, fieldiq-api#939): approved tickets → PTI invoices — the
  // Ready-to-invoice queue and the generate flow. "PT Invoices", not "Invoices":
  // a PT tenant also has FINANCE → Invoices (the all-verticals list) since
  // SHARED_ITEM_VERTICALS.invoices was widened. FileText, as Certificates.
  // Since 2026-10-30 (Jason) the item is "PT Ready to invoice": FINANCE → Invoices
  // lists the PTI- invoices, and this page's job in the sidebar is the queue.
  // The prefix stays because a PT + NDT tenant's More merges both lists.
  { id: 'pt-invoices', label: 'PT Ready to invoice', icon: 'FileText', path: '/pressure-testing/invoices', vertical: 'pressure_testing' },

  // ── NDT / Inspections batch 1 (fieldiq-api#1134). Icons from the existing
  // sidebar sets, so neither icon map changes. "NDT Instruments", not
  // "Instruments": a PT + NDT tenant would otherwise see two items of one name. ──
  // Batch 2 (fieldiq-api#1144): the work itself — jobs, then the weld register.
  // "NDT Jobs", not "Jobs": a casing-shaped name would read as the casing job list.
  { id: 'ndt-jobs', label: 'NDT Jobs', icon: 'ClipboardList', path: '/ndt-inspection/jobs', vertical: 'ndt_inspection' },
  { id: 'ndt-welds', label: 'Welds', icon: 'Layers', path: '/ndt-inspection/welds', vertical: 'ndt_inspection' },
  // Batch 3 (fieldiq-api#1152). "NDT Reports", not "Reports": the shared
  // Reports item (the analytics catalog) would otherwise appear twice by name.
  { id: 'ndt-reports', label: 'NDT Reports', icon: 'FileText', path: '/ndt-inspection/reports', vertical: 'ndt_inspection' },
  // Batch 4 (fieldiq-api#1157). "NDT Field Tickets", not "Field Tickets":
  // surface rentals' register already has that name, so an NDT + rentals tenant
  // would see two (same rule as NDT Jobs / NDT Reports). Receipt is PT's ticket icon.
  { id: 'ndt-tickets', label: 'NDT Field Tickets', icon: 'Receipt', path: '/ndt-inspection/tickets', vertical: 'ndt_inspection' },
  // Batch 5 (fieldiq-api#1166): approved tickets → NDI- invoices — the
  // Ready-to-invoice queue and the create flow. "NDT Invoices", not "Invoices":
  // FINANCE → Invoices (the all-verticals list) shows for NDT too since batch 5.
  // "NDT Ready to invoice" since 2026-10-30 (Jason), as PT's. The prefix is
  // load-bearing: a PT + NDT tenant's More merges both lists into one section.
  { id: 'ndt-invoices', label: 'NDT Ready to invoice', icon: 'FileText', path: '/ndt-inspection/invoices', vertical: 'ndt_inspection' },
  { id: 'ndt-technicians', label: 'Technicians & Certs', icon: 'Users', path: '/ndt-inspection/technicians', vertical: 'ndt_inspection' },
  { id: 'ndt-welders', label: 'Welders & Quals', icon: 'Hammer', path: '/ndt-inspection/welders', vertical: 'ndt_inspection' },
  { id: 'ndt-instruments', label: 'NDT Instruments', icon: 'ClipboardCheck', path: '/ndt-inspection/instruments', vertical: 'ndt_inspection' },
  { id: 'ndt-sources', label: 'Sources & Radiation', icon: 'Shield', path: '/ndt-inspection/sources', vertical: 'ndt_inspection' },
  { id: 'ndt-dosimetry', label: 'Dosimetry', icon: 'Clock', path: '/ndt-inspection/dosimetry', vertical: 'ndt_inspection' },
  // Batch 6 (fieldiq-api#1177 / #1178): the AI digests. "NDT AI Digests", not
  // "AI Digests", for the same one-name-per-page reason as NDT Jobs. Bot is the
  // agent's icon, already in the sidebar map.
  { id: 'ndt-ai', label: 'NDT AI Digests', icon: 'Bot', path: '/ndt-inspection/ai', vertical: 'ndt_inspection' },
  // Quotes under SALES for an NDT tenant (Jason, 2026-10-24): the same /quotes
  // page as FINANCE → Quotes. SALES resolves before FINANCE, so this entry claims
  // the path and FINANCE's copy drops (one entry per page); every tenant without
  // NDT resolves this to nothing and keeps Quotes in FINANCE, unchanged.
  { id: 'ndt-quotes', label: 'Quotes', icon: 'FileText', path: '/quotes', vertical: 'ndt_inspection' },

  // ── Pipeline & Facility PF-1 (fieldiq-api#1196). Icons from the existing
  // sidebar sets. Sites: the customer's pipelines and facilities, and a
  // pipeline's segments. ──
  { id: 'pf-sites', label: 'Sites', icon: 'MapPin', path: '/pipeline-facility/sites', vertical: 'pipeline_facility' },
  // The shared /equipment register (DESIGN.md §g: Pipeline owns no equipment
  // table, so no fork). The COMPANY row has no Equipment item on this sidebar,
  // so this is the only entry for the path.
  { id: 'pf-equipment', label: 'Equipment', icon: 'Wrench', path: '/equipment', vertical: 'pipeline_facility' },
  // PF-2 (fieldiq-api#1200): jobs — crew, units, rent-ins, daily logs.
  { id: 'pf-jobs', label: 'Jobs', icon: 'ClipboardList', path: '/pipeline-facility/jobs', vertical: 'pipeline_facility' },
  // The shared Equipment Maintenance screen (DESIGN.md §g: no fork). Not
  // vertical-guarded as a route; this entry is only the way in for a PF tenant.
  { id: 'pf-maintenance', label: 'Maintenance', icon: 'Wrench', path: '/reports/equipment-maintenance', vertical: 'pipeline_facility' },
  // PF-3a (fieldiq-api#1219): integrity digs — the ILI anomaly list, the digs
  // planned from it, the welds on their repairs, and the dig reports. Icons
  // from the existing sidebar sets. "Dig Reports", not "Reports": REPORTS is
  // a section of its own.
  { id: 'pf-anomalies', label: 'Anomalies', icon: 'Layers', path: '/pipeline-facility/anomalies', vertical: 'pipeline_facility' },
  { id: 'pf-digs', label: 'Digs', icon: 'ClipboardCheck', path: '/pipeline-facility/digs', vertical: 'pipeline_facility' },
  { id: 'pf-welds', label: 'Welds', icon: 'Hammer', path: '/pipeline-facility/welds', vertical: 'pipeline_facility' },
  { id: 'pf-dig-reports', label: 'Dig Reports', icon: 'FileText', path: '/pipeline-facility/dig-reports', vertical: 'pipeline_facility' },
  // PF-4 (fieldiq-api#1227): field tickets drafted from the daily logs, and
  // the rate card that prices them. "Rate Card" sits in the FINANCE row beside
  // Tool Rates; "Field Tickets" in OPERATIONS after Jobs, where the work runs.
  { id: 'pf-tickets', label: 'Field Tickets', icon: 'Receipt', path: '/pipeline-facility/tickets', vertical: 'pipeline_facility' },
  // "Pipeline Invoices", not "Invoices" (PF-5c, fieldiq-api#1232): FINANCE →
  // Invoices (the all-verticals list) shows for Pipeline too now — NDT's rule.
  // "Ready to invoice" since 2026-10-30 (Jason), and it opens Pipeline's own
  // ready queue — the invoice list is FINANCE → Invoices.
  { id: 'pf-invoices', label: 'Ready to invoice', icon: 'FileText', path: '/pipeline-facility/invoices/ready', vertical: 'pipeline_facility' },
  { id: 'pf-rates', label: 'Rate Card', icon: 'DollarSign', path: '/pipeline-facility/rates', vertical: 'pipeline_facility' },
  // PF-6 (fieldiq-api#1236): the AI drafts — daily summaries, preflights, the
  // compliance digest. Bot is the agent's icon, already in the sidebar map.
  { id: 'pf-ai', label: 'AI Drafts', icon: 'Bot', path: '/pipeline-facility/ai', vertical: 'pipeline_facility' },

  // ── Well Testing WT-1 (fieldiq-api#1241). The well register; jobs, readings
  // and tickets join this group as WT-2 onward ships. "Wells" is also the
  // downhole Directional & MWD register's label, at a different path — a
  // tenant with both reads them under two group headings. ──
  { id: 'wt-wells', label: 'Wells', icon: 'MapPin', path: '/well-testing/wells', vertical: 'well_testing' },
  { id: 'wt-jobs', label: 'Jobs', icon: 'ClipboardList', path: '/well-testing/jobs', vertical: 'well_testing' },
  { id: 'wt-packages', label: 'Packages', icon: 'Package', path: '/well-testing/packages', vertical: 'well_testing' },
  { id: 'wt-equipment', label: 'Equipment', icon: 'Wrench', path: '/equipment', vertical: 'well_testing' },
  { id: 'wt-maintenance', label: 'Maintenance', icon: 'Wrench', path: '/reports/equipment-maintenance', vertical: 'well_testing' },
  { id: 'wt-ai', label: 'AI Drafts', icon: 'Bot', path: '/well-testing/ai', vertical: 'well_testing' },
  { id: 'wt-tickets', label: 'Field Tickets', icon: 'Receipt', path: '/well-testing/tickets', vertical: 'well_testing' },
  { id: 'wt-rates', label: 'Rate Card', icon: 'DollarSign', path: '/well-testing/rates', vertical: 'well_testing' },
  // The approved-tickets queue and New invoice; WTI- invoices list in FINANCE →
  // Invoices (2026-10-30, Jason). Until then no sidebar item reached this page.
  { id: 'wt-ready', label: 'Ready to invoice', icon: 'FileText', path: '/well-testing/invoices', vertical: 'well_testing' },

  // ── Earthworks EW-1. Its own group, EARTHWORKS: jobs, sites and the rate
  // card, then the shared Customers, Equipment and Maintenance screens (no
  // forks). Customers is listed here so an earthworks tenant finds it beside
  // the sites it owns; the resolver then drops COMPANY's Clients (same path). ──
  { id: 'ew-jobs', label: 'Jobs', icon: 'ClipboardList', path: '/earthworks/jobs', vertical: 'earthworks' },
  { id: 'ew-sites', label: 'Sites', icon: 'MapPin', path: '/earthworks/sites', vertical: 'earthworks' },
  { id: 'ew-rates', label: 'Rate Card', icon: 'DollarSign', path: '/earthworks/rates', vertical: 'earthworks' },
  // EW-3: AI-drafted quotes from building photos or plans (drafts until a person creates the quote).
  { id: 'ew-ai-quotes', label: 'AI Quotes', icon: 'Bot', path: '/earthworks/ai-quotes', vertical: 'earthworks' },
  // EW-8: completed jobs against their quotes, by job type; suggestions for the AI-quote defaults.
  { id: 'ew-qva', label: 'Quoted vs Actual', icon: 'TrendingUp', path: '/earthworks/quoted-vs-actual', vertical: 'earthworks' },
  // EW-5: field tickets (built from daily logs, signed on site, office-approved) and change orders.
  { id: 'ew-tickets', label: 'Tickets', icon: 'FileText', path: '/earthworks/tickets', vertical: 'earthworks' },
  { id: 'ew-change-orders', label: 'Change Orders', icon: 'ScrollText', path: '/earthworks/change-orders', vertical: 'earthworks' },
  // EW-6 (fieldiq-api#1300): approved tickets → EWI- invoices. The invoices
  // list in FINANCE → Invoices, so this item is the queue, "Ready to invoice",
  // as every other vertical's is (Jason, 2026-10-30).
  { id: 'ew-ready', label: 'Ready to invoice', icon: 'FileText', path: '/earthworks/invoices', vertical: 'earthworks' },
  { id: 'ew-customers', label: 'Customers', icon: 'Building2', path: '/customers', vertical: 'earthworks' },
  { id: 'ew-equipment', label: 'Equipment', icon: 'Wrench', path: '/equipment', vertical: 'earthworks' },
  { id: 'ew-maintenance', label: 'Maintenance', icon: 'Wrench', path: '/reports/equipment-maintenance', vertical: 'earthworks' },

  // ── Well Servicing WS-1 (fieldiq-api#1269). Its own group, WELL SERVICING:
  // the wells register, rigs with their crew templates, and the activity
  // codes; then the shared Pressure Control register (BOP certificates, BOP-1,
  // which a service rig's stack is certified in) and the shared Customers,
  // Equipment and Maintenance screens (no forks). ──
  { id: 'ws-jobs', label: 'Jobs', icon: 'ClipboardList', path: '/well-servicing/jobs', vertical: 'well_servicing' },
  { id: 'ws-wells', label: 'Wells', icon: 'MapPin', path: '/well-servicing/wells', vertical: 'well_servicing' },
  { id: 'ws-rigs', label: 'Rigs', icon: 'Truck', path: '/well-servicing/rigs', vertical: 'well_servicing' },
  { id: 'ws-codes', label: 'Activity Codes', icon: 'ClipboardList', path: '/well-servicing/activity-codes', vertical: 'well_servicing' },
  { id: 'ws-tickets', label: 'Tickets', icon: 'Receipt', path: '/well-servicing/tickets', vertical: 'well_servicing' },
  { id: 'ws-rates', label: 'Rate card', icon: 'DollarSign', path: '/well-servicing/rates', vertical: 'well_servicing' },
  // WS-5 (fieldiq-api#1295): the approved-tickets queue and New invoice. The
  // INV- invoices themselves list in FINANCE → Invoices, the one invoice list
  // for every vertical (2026-10-30, Jason) — so this item is "Ready to invoice",
  // as Well Testing's is, not a second "Invoices".
  { id: 'ws-ready', label: 'Ready to invoice', icon: 'FileText', path: '/well-servicing/invoices', vertical: 'well_servicing' },
  { id: 'ws-pressure-control', label: 'Pressure Control', icon: 'Shield', path: '/equipment/pressure-control', vertical: 'well_servicing' },
  { id: 'ws-customers', label: 'Customers', icon: 'Building2', path: '/customers', vertical: 'well_servicing' },
  { id: 'ws-equipment', label: 'Equipment', icon: 'Wrench', path: '/equipment', vertical: 'well_servicing' },
  { id: 'ws-maintenance', label: 'Maintenance', icon: 'Wrench', path: '/reports/equipment-maintenance', vertical: 'well_servicing' },
  // WS-6 (fieldiq-api#1296): the AI drafts register.
  { id: 'ws-ai', label: 'AI Drafts', icon: 'Bot', path: '/well-servicing/ai', vertical: 'well_servicing' },

  // ── Wireline & Slickline WL-1 (fieldiq-api#1336). Its own group, WIRELINE &
  // SLICKLINE: the overview, the shared wells register, units with their crews,
  // lines with their event log, tool strings; then the shared Customers and
  // Equipment screens (no forks). ──
  { id: 'wl-overview', label: 'Overview', icon: 'LayoutDashboard', path: '/wireline-slickline', vertical: 'wireline_slickline' },
  { id: 'wl-jobs', label: 'Jobs', icon: 'ClipboardList', path: '/wireline-slickline/jobs', vertical: 'wireline_slickline' },
  // WL-3 (api#1339). "Service Reports", not "Reports": the generic Reports item is a different screen.
  { id: 'wl-reports', label: 'Service Reports', icon: 'FileText', path: '/wireline-slickline/reports', vertical: 'wireline_slickline' },
  { id: 'wl-wells', label: 'Wells', icon: 'MapPin', path: '/wireline-slickline/wells', vertical: 'wireline_slickline' },
  { id: 'wl-units', label: 'Units', icon: 'Truck', path: '/wireline-slickline/units', vertical: 'wireline_slickline' },
  { id: 'wl-lines', label: 'Lines', icon: 'Layers', path: '/wireline-slickline/lines', vertical: 'wireline_slickline' },
  { id: 'wl-tool-strings', label: 'Tool Strings', icon: 'Wrench', path: '/wireline-slickline/tool-strings', vertical: 'wireline_slickline' },
  { id: 'wl-customers', label: 'Customers', icon: 'Building2', path: '/customers', vertical: 'wireline_slickline' },
  { id: 'wl-equipment', label: 'Equipment', icon: 'Wrench', path: '/equipment', vertical: 'wireline_slickline' },
];

/**
 * Sidebar items that are active only on their own path, never on a page under
 * it: a landing page whose path prefixes its section's other items. Without
 * this, /wireline-slickline/lines/:id lit both Overview (/wireline-slickline)
 * and Lines.
 */
export const SIDEBAR_EXACT_MATCH_PATHS: readonly string[] = ['/', '/rentals', '/trucking', '/downhole', '/wireline-slickline'];

/** Whether a sidebar item is the current page: exact for the paths above, else the path or anything under it. */
export function isSidebarItemActive(itemPath: string, pathname: string): boolean {
  if (SIDEBAR_EXACT_MATCH_PATHS.includes(itemPath)) return pathname === itemPath;
  return pathname === itemPath || pathname.startsWith(itemPath + '/');
}

export type VerticalSidebarSection =
  | 'FAVORITES'
  | 'OPERATIONS'
  | 'WELL TESTING'
  | 'EARTHWORKS'
  | 'WELL SERVICING'
  | 'WIRELINE & SLICKLINE'
  | 'REPORTS'
  | 'TRUCKING TOOLS'
  | 'TRUCKS & DRIVERS'
  | 'RENTAL TOOLS'
  | 'COMPANY'
  | 'SALES'
  | 'FINANCE'
  | 'SYSTEM';

/**
 * How a group is drawn.
 *
 *  - `section` — a small-caps heading with its items always shown under it. Not collapsible:
 *    FAVORITES, OPERATIONS and REPORTS are the working set and are never hidden.
 *  - `row` — a single nav-sized row with a chevron and NO heading above it, sitting at the
 *    bottom of the list, shut until opened. This is the Manus layout: the day-to-day screens
 *    on top, everything else folded into five rows at the foot of the sidebar.
 */
export type VerticalSidebarGroupKind = 'section' | 'row';

export interface VerticalSidebarGroupSpec {
  section: VerticalSidebarSection;
  kind: VerticalSidebarGroupKind;
  /** The row's visible name. Only a `row` group uses it; a section shows `section`. */
  rowLabel?: string;
  /** lucide-react icon name for a `row` group. */
  rowIcon?: string;
  /** Starting state. Only a `row` group can be shut; a `section` is always open. */
  defaultCollapsed: boolean;
  /** An id from VERTICAL_NAV or, for a shared item, from CASING_NAV. */
  items: { id: string; label?: string }[];
}

export const VERTICAL_SIDEBAR_STRUCTURE: VerticalSidebarGroupSpec[] = [
  {
    section: 'FAVORITES',
    kind: 'section',
    defaultCollapsed: false,
    items: [
      { id: 'overview' },
      { id: 'trucking-dispatch' },
      { id: 'rentals-dispatch' },
      { id: 'downhole' },
      { id: 'downhole-assemblies' },
      // Bills of Lading is a daily screen, so it is a favourite and reaches the
      // phone bar (FAVORITES feeds it). LAST, so the favourites every other
      // vertical already has keep their places: a trucking + rentals tenant's
      // bar is Overview, Dispatch, Board, BOLs; a trucking + downhole tenant's
      // fifth favourite folds into More, as before.
      { id: 'trucking-bols' },
      // NDT's first working screen (who may shoot), so an NDT tenant's phone
      // bar holds an NDT page, not only Overview. LAST, for the same reason as
      // BOLs: no other vertical's favourites move. It leaves OPERATIONS for NDT
      // tenants only (one entry per page); the rest of the NDT group stays there,
      // and is the first More section on a phone.
      { id: 'ndt-technicians' },
      // Pipeline & Facility's first working screen, LAST for the same reason.
      { id: 'pf-sites' },
    ],
  },
  {
    section: 'OPERATIONS',
    kind: 'section',
    defaultCollapsed: false,
    items: [
      // Trucking's day-to-day screens. Fleet and Drivers are set-up registers
      // and sit in the Fleet & drivers row below (fix/trucking-nav).
      { id: 'trucking-calendar' },
      { id: 'trucking-weight-tickets' },
      { id: 'trucking-move-jobs' },
      { id: 'trucking-trip-inspections' },
      // Rentals in the operator's order (2026-10-15): orders, the yard, the
      // equipment register, inspections, damage charges.
      { id: 'rentals-active' },
      { id: 'rentals-yard' },
      { id: 'rentals-assets' },
      { id: 'rentals-inspections' },
      { id: 'rentals-damage-charges' },
      // Wells first among the downhole screens: the well is what the rest of
      // the work hangs off. Not in FAVORITES, which feeds the phone bar.
      { id: 'downhole-wells' },
      { id: 'downhole-components' },
      { id: 'downhole-templates' },
      { id: 'downhole-run-logs' },
      { id: 'downhole-tickets' },
      { id: 'downhole-work-orders' },
      // Pressure testing in the order the work runs (operator, 2026-10-08):
      // the job, its field ticket, the certificate, then the instruments and
      // their calibration alerts, and the invoice last.
      { id: 'pt-jobs' },
      { id: 'pt-tickets' },
      { id: 'pt-certificates' },
      { id: 'pt-instruments' },
      { id: 'pt-alerts' },
      { id: 'pt-invoices' },
      // NDT: the work first (batch 2 — jobs, then welds; batch 3 — the
      // reports they produce), then the people
      // (who may shoot, whose welds), the gear and the sources, then dose.
      { id: 'ndt-jobs' },
      { id: 'ndt-welds' },
      { id: 'ndt-reports' },
      { id: 'ndt-tickets' },
      { id: 'ndt-invoices' },
      { id: 'ndt-technicians' },
      { id: 'ndt-welders' },
      { id: 'ndt-instruments' },
      { id: 'ndt-sources' },
      { id: 'ndt-dosimetry' },
      // Last: the digests read across everything above.
      { id: 'ndt-ai' },
      // Pipeline & Facility: Sites is in FAVORITES; jobs, then the shared
      // equipment register and its maintenance screen.
      { id: 'pf-jobs' },
      // PF-4: a job's field tickets come right after the job.
      { id: 'pf-tickets' },
      { id: 'pf-invoices' },
      // PF-3a: the dig program in the order the work runs — anomalies, digs,
      // welds, the reports — between jobs and the shared equipment screens.
      { id: 'pf-anomalies' },
      { id: 'pf-digs' },
      { id: 'pf-welds' },
      { id: 'pf-dig-reports' },
      { id: 'pf-equipment' },
      { id: 'pf-maintenance' },
      // PF-6: last — the drafts read across everything above.
      { id: 'pf-ai' },
    ],
  },
  {
    // Well Testing's own working screens, under its own heading (WT-1: Wells).
    // A section, so never shut. Resolves empty, and is not drawn, for every
    // tenant without well_testing.
    section: 'WELL TESTING',
    kind: 'section',
    defaultCollapsed: false,
    items: [{ id: 'wt-wells' }, { id: 'wt-jobs' }, { id: 'wt-tickets' }, { id: 'wt-ready' }, { id: 'wt-rates' }, { id: 'wt-packages' }, { id: 'wt-equipment' }, { id: 'wt-maintenance' }, { id: 'wt-ai' }],
  },
  {
    // Earthworks' own working screens (EW-1). A section, so never shut.
    // Resolves empty, and is not drawn, for every tenant without earthworks.
    section: 'EARTHWORKS',
    kind: 'section',
    defaultCollapsed: false,
    items: [
      { id: 'ew-jobs' },
      { id: 'ew-sites' },
      { id: 'ew-rates' },
      { id: 'ew-ai-quotes' },
      { id: 'ew-qva' },
      { id: 'ew-tickets' },
      { id: 'ew-change-orders' },
      { id: 'ew-ready' },
      { id: 'ew-customers' },
      { id: 'ew-equipment' },
      { id: 'ew-maintenance' },
    ],
  },
  {
    // Well Servicing's own working screens (WS-1). A section, so never shut.
    // Resolves empty, and is not drawn, for every tenant without well servicing.
    section: 'WELL SERVICING',
    kind: 'section',
    defaultCollapsed: false,
    items: [
      { id: 'ws-jobs' },
      { id: 'ws-wells' },
      { id: 'ws-rigs' },
      { id: 'ws-codes' },
      { id: 'ws-tickets' },
      { id: 'ws-ready' },
      { id: 'ws-rates' },
      { id: 'ws-pressure-control' },
      { id: 'ws-customers' },
      { id: 'ws-equipment' },
      { id: 'ws-maintenance' },
      { id: 'ws-ai' },
    ],
  },
  {
    // Wireline & Slickline's own working screens (WL-1). A section, so never
    // shut. Resolves empty, and is not drawn, for every tenant without it.
    section: 'WIRELINE & SLICKLINE',
    kind: 'section',
    defaultCollapsed: false,
    items: [
      { id: 'wl-overview' },
      { id: 'wl-jobs' },
      { id: 'wl-reports' },
      { id: 'wl-wells' },
      { id: 'wl-units' },
      { id: 'wl-lines' },
      { id: 'wl-tool-strings' },
      { id: 'wl-customers' },
      { id: 'wl-equipment' },
    ],
  },
  {
    section: 'REPORTS',
    kind: 'section',
    defaultCollapsed: false,
    items: [{ id: 'reports' }, { id: 'downhole-reports' }],
  },
  {
    // Trucking's set-up registers: the units and the people who drive them.
    // Below the working sections, with the other rows. The /trucking dashboard
    // that led this row is retired; Calendar, Weight Tickets and Move Jobs moved
    // up to OPERATIONS. The section key keeps its name so the phone nav's guard
    // and the stored collapse state still find it.
    section: 'TRUCKING TOOLS',
    kind: 'row',
    rowLabel: 'Fleet & drivers',
    rowIcon: 'Truck',
    defaultCollapsed: true,
    items: [{ id: 'trucking-fleet' }, { id: 'trucking-drivers' }],
  },
  {
    // A rentals tenant WITHOUT trucking: its trucks, drivers and their trip
    // inspections, out of OPERATIONS and folded here. With trucking the three
    // are already claimed by OPERATIONS, so this row resolves empty and is not
    // drawn (see the docblock at the top).
    section: 'TRUCKS & DRIVERS',
    kind: 'row',
    rowLabel: 'Trucks & Drivers',
    rowIcon: 'Truck',
    defaultCollapsed: true,
    items: [{ id: 'rentals-fleet' }, { id: 'rentals-drivers' }, { id: 'rentals-trip-inspections' }],
  },
  {
    section: 'RENTAL TOOLS',
    kind: 'row',
    rowLabel: 'Rental tools',
    rowIcon: 'Package',
    defaultCollapsed: true,
    items: [
      // The Rentals dashboard is retired (2026-10-15); its figures are the
      // Overview's Rentals Summary widget.
      { id: 'rentals-tickets' },
      { id: 'rentals-maintenance' },
      { id: 'rentals-price-books' },
    ],
  },
  {
    // The cross-vertical company screens. They were the tail of OPERATIONS; they moved here
    // so OPERATIONS is only the vertical's own working screens.
    section: 'COMPANY',
    kind: 'row',
    rowLabel: 'Company',
    rowIcon: 'Building2',
    defaultCollapsed: true,
    items: [
      // No Equipment (sweep batch 9, operator's call): for a trucking / rentals tenant it
      // read as a duplicate of Fleet and Rental Equipment, and a "vehicle" added there
      // never reaches either. The /equipment route is cross-vertical and stays; a tenant
      // with none of the three verticals (the legacy sidebar) and casing keep the link.
      { id: 'clients' },
      { id: 'team' },
      { id: 'time-off' },
      { id: 'safety' },
    ],
  },
  {
    // The CRM (sales-log D11) — cross-vertical, CRM_ROLES only (the nav filter
    // in lib/sales-access.ts drops it for every other role).
    section: 'SALES',
    kind: 'row',
    rowLabel: 'Sales',
    rowIcon: 'Handshake',
    defaultCollapsed: true,
    // Clients stays in COMPANY for these tenants: one page, one entry.
    // ndt-quotes: NDT tenants only (see VERTICAL_NAV). /quotes is not a CRM
    // path, so it is drawn for every role that sees Quotes today.
    items: [{ id: 'ndt-quotes' }, { id: 'sales-meetings' }, { id: 'sales-pipeline' }],
  },
  {
    section: 'FINANCE',
    kind: 'row',
    rowLabel: 'Finance',
    rowIcon: 'DollarSign',
    defaultCollapsed: true,
    items: [
      { id: 'rates', label: 'Rate Sheets' },
      // Where Rate Sheets sat for a trucking tenant, which now has its own card.
      { id: 'trucking-rates' },
      { id: 'quotes' },
      { id: 'invoices' },
      { id: 'downhole-invoices' },
      { id: 'downhole-rates' },
      { id: 'pf-rates' },
      { id: 'expenses' },
      { id: 'payroll' },
      { id: 'timesheet-approvals', label: 'Timesheets' },
    ],
  },
  {
    section: 'SYSTEM',
    kind: 'row',
    rowLabel: 'System',
    rowIcon: 'Settings',
    defaultCollapsed: true,
    items: [{ id: 'alerts' }, { id: 'settings' }],
  },
];

/**
 * THE SHARED FINANCE ITEMS SHOW ONLY FOR A TENANT WITH A VERTICAL THAT USES THEM
 * (operator-approved, 2026-10-07). Each list names the verticals of THIS sidebar
 * whose work reaches the screen; an item whose list shares nothing with the
 * tenant's verticals is not rendered. The route itself stays reachable by URL.
 * Measured, not assumed (file:line in the FE-5 PR):
 *
 *   rates (Rate Sheets)      none since api#950: trucking prices loads from its
 *                            own card (trucking_rates); rentals use price books,
 *                            downhole dh_rates, PT none
 *   quotes                   only casing writes quotes; no vertical here links one
 *   invoices                 /finance/invoices is the ONE invoice list across
 *                            verticals (decided April 2026): casing rows from
 *                            GET /invoices, every other billing vertical's from
 *                            GET /unified-invoices. So every billing vertical
 *                            shows it. Each vertical's own page stays in the
 *                            sidebar as "Ready to invoice" (the generate flows).
 *   payroll                  every vertical's clock-in can feed it (approved shop
 *                            shifts, services/shop-pay.service.ts)
 *   timesheet-approvals      lists the `timesheets` table, written by clock-in,
 *                            which every vertical's field hands reach
 *
 * Expenses carries no list: it stays for every tenant. A shared item with no
 * entry here is unguarded, as before. A casing tenant never uses this sidebar.
 */
export const SHARED_ITEM_VERTICALS: Record<string, readonly NavVertical[]> = {
  // Trucking was the ONLY vertical on this sidebar that priced from
  // rate_sheets, and since api#950 it has its own card (Trucking Rates), so
  // the list is empty: the item left exactly the tenants that had trucking.
  // Rentals, downhole and PT never saw it. EMPTY, not deleted: an id with no
  // entry here is unguarded and would show Rate Sheets to every tenant on this
  // sidebar. A casing tenant (casing + trucking included) uses
  // CASING_SIDEBAR_STRUCTURE instead, where Rate Sheets is unconditional.
  rates: [],
  // Quotes come back per vertical as each pricing source ships (api lib/quote-pricing).
  // NDT joined both with batch 5 (fieldiq-api#1166): quotes price from its rate
  // card, and its NDI- invoices list in Finance → Invoices (Jason, 2026-10-24).
  // Pipeline joined both with PF-5c (fieldiq-api#1232): quotes price from the
  // Pipeline Rate Card, and its PFI- invoices list in Finance → Invoices.
  // Well Testing joined Quotes with WT-5b (fieldiq-api#1258): quotes price from
  // the Well Testing Rate Card, and its WTI- invoices list in Finance → Invoices
  // since 2026-10-30.
  // Earthworks joined Quotes with EW-2: quotes price from the Earthworks Rate Card.
  // Well Servicing joined both with WS-5 (fieldiq-api#1295): quotes price from
  // the Well Servicing Rate Card, and its INV- invoices list in Finance → Invoices.
  quotes: ['surface_rentals', 'downhole_tools', 'pressure_testing', 'ndt_inspection', 'pipeline_facility', 'well_testing', 'earthworks', 'well_servicing'],
  // Earthworks joined Invoices with EW-6 (fieldiq-api#1300): its EWI- invoices
  // list in Finance → Invoices.
  invoices: ['trucking', 'surface_rentals', 'downhole_tools', 'pressure_testing', 'ndt_inspection', 'pipeline_facility', 'well_testing', 'well_servicing', 'earthworks'],
  // NDT's field hands clock in like every other vertical's, so Payroll and
  // Timesheets show for it. Earthworks joined both when its jobs became
  // clockable (feat/ew-employee-hours): a crewed hand clocks in on an EWJ job.
  payroll: ['trucking', 'surface_rentals', 'downhole_tools', 'pressure_testing', 'ndt_inspection', 'earthworks'],
  'timesheet-approvals': ['trucking', 'surface_rentals', 'downhole_tools', 'pressure_testing', 'ndt_inspection', 'earthworks'],
};

export interface ResolvedVerticalNavItem {
  id: string;
  label: string;
  icon: string;
  path: string;
}

export interface ResolvedVerticalGroup {
  section: VerticalSidebarSection;
  kind: VerticalSidebarGroupKind;
  rowLabel?: string;
  rowIcon?: string;
  defaultCollapsed: boolean;
  items: ResolvedVerticalNavItem[];
}

/**
 * Resolve VERTICAL_SIDEBAR_STRUCTURE for one tenant.
 *
 * `truckingFeatures` is GET /settings/trucking. Undefined (not loaded, or not a trucking
 * tenant) shows every trucking screen, which is what the sidebar did before.
 *
 * `downholeFeatures` is the opposite: an item with a `downholeFeature` shows only
 * when that mode is delivered as true. Undefined (not loaded, failed, not a
 * downhole tenant) hides it.
 */
export function buildVerticalSidebarStructure(
  hasVertical: (key: string) => boolean,
  truckingFeatures?: Partial<Record<TruckingFeatureFlag, unknown>> | null,
  downholeFeatures?: Partial<Record<DownholeFeatureFlag, boolean>> | null,
): ResolvedVerticalGroup[] {
  const verticalById = new Map(VERTICAL_NAV.map((i) => [i.id, i]));
  const sharedById = new Map(CASING_NAV.map((i) => [i.id, i]));
  const claimed = new Set<string>();

  const resolve = (entry: { id: string; label?: string }): ResolvedVerticalNavItem | null => {
    const own = verticalById.get(entry.id);
    if (own) {
      if (own.vertical && !(typeof own.vertical === 'string' ? [own.vertical] : own.vertical).some((v) => hasVertical(v))) return null;
      if (own.feature && truckingFeatures?.[own.feature] === false) return null;
      if (own.downholeFeature && downholeFeatures?.[own.downholeFeature] !== true) return null;
      return { id: own.id, label: entry.label ?? own.label, icon: own.icon, path: own.path };
    }
    const shared = sharedById.get(entry.id);
    if (!shared) {
      throw new Error(`verticalNav: VERTICAL_SIDEBAR_STRUCTURE references unknown nav id "${entry.id}"`);
    }
    const usedBy = SHARED_ITEM_VERTICALS[entry.id];
    if (usedBy && !usedBy.some((v) => hasVertical(v))) return null;
    return { id: shared.id, label: entry.label ?? shared.label, icon: shared.icon, path: shared.path };
  };

  return VERTICAL_SIDEBAR_STRUCTURE.map((group) => ({
    section: group.section,
    kind: group.kind,
    rowLabel: group.rowLabel,
    rowIcon: group.rowIcon,
    // A section cannot be shut, whatever the spec says.
    defaultCollapsed: group.kind === 'row' && group.defaultCollapsed,
    items: group.items
      .map(resolve)
      .filter((item): item is ResolvedVerticalNavItem => {
        if (!item || claimed.has(item.path)) return false;
        claimed.add(item.path);
        return true;
      }),
  })).filter((g) => g.items.length > 0);
}
